/**
 * Top-right tactic actions menu — shareable code + library save/load.
 *
 * Replaces the two old "Copy code" / "Paste code" chips that used to
 * live in the Design row. Coach feedback: the row was too crowded and
 * the word "code" was jargon. A single "Tactic ▾" button in the
 * board's top-right corner now expands to a small popover with:
 *
 *   - Copy tactic     — encodes the current layout to a short
 *                       URL-safe string + writes to clipboard.
 *   - Paste tactic    — reads clipboard + applies if it parses.
 *   - Save to library — prompts for a name + saves via
 *                       tacticsPatternsStore.savePattern. Coach can
 *                       then recall it from the Patterns drawer.
 *   - Load from library — opens the Patterns drawer.
 *
 * The button is positioned absolutely within whatever container
 * wraps it; the caller is responsible for ensuring that container
 * is `position: relative`. The popover dismisses on Escape +
 * click-outside.
 */

import { useEffect, useRef, useState } from 'react';
import './TacticMenu.css';

export interface TacticMenuProps {
  onCopy: () => void | Promise<void>;
  onPaste: () => void | Promise<void>;
  onSave: (name: string) => void;
  onLoad: () => void;
}

export function TacticMenu({ onCopy, onPaste, onSave, onLoad }: TacticMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointer = (e: PointerEvent) => {
      const root = rootRef.current;
      if (!root) return;
      if (e.target instanceof Node && root.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    // Defer one tick so the click that opened the menu doesn't
    // immediately count as a click-outside.
    const t = setTimeout(() => {
      window.addEventListener('pointerdown', onPointer);
    }, 0);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointer);
      clearTimeout(t);
    };
  }, [open]);

  const handleSave = () => {
    const name = window.prompt('Save tactic as:', 'My tactic');
    if (!name || !name.trim()) return;
    onSave(name.trim());
    setOpen(false);
  };

  return (
    <div className="tactic-menu" ref={rootRef} data-testid="tactic-menu">
      <button
        type="button"
        className="tactic-menu__button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Tactic actions"
        onClick={() => setOpen((o) => !o)}
      >
        Tactic <span aria-hidden="true" className="tactic-menu__chevron">▾</span>
      </button>
      {open && (
        <div
          className="tactic-menu__popover"
          role="menu"
          aria-label="Tactic actions"
          data-testid="tactic-menu-popover"
        >
          <button
            type="button"
            role="menuitem"
            className="tactic-menu__item"
            onClick={() => {
              // Close synchronously so the popover dismisses before
              // any clipboard round-trip finishes; the action itself
              // runs in the background. UX-wise: tap → menu vanishes.
              setOpen(false);
              void Promise.resolve(onCopy());
            }}
          >
            <span className="tactic-menu__item-label">Copy tactic</span>
            <span className="tactic-menu__item-meta">to clipboard</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="tactic-menu__item"
            onClick={() => {
              setOpen(false);
              void Promise.resolve(onPaste());
            }}
          >
            <span className="tactic-menu__item-label">Paste tactic</span>
            <span className="tactic-menu__item-meta">from clipboard</span>
          </button>
          <div className="tactic-menu__divider" role="separator" />
          <button
            type="button"
            role="menuitem"
            className="tactic-menu__item"
            onClick={handleSave}
          >
            <span className="tactic-menu__item-label">Save to library</span>
            <span className="tactic-menu__item-meta">named pattern</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="tactic-menu__item"
            onClick={() => {
              onLoad();
              setOpen(false);
            }}
          >
            <span className="tactic-menu__item-label">Load from library</span>
            <span className="tactic-menu__item-meta">famous + saved</span>
          </button>
        </div>
      )}
    </div>
  );
}
